import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import AppBar from '../components/AppBar'
import CustomSelect, { SelectOption } from '../components/CustomSelect'
import ExperienceButtons from '../components/ExperienceButtons'
import SubmitButton from '../components/SubmitButton'
import FormErrorMessage from '../components/FormErrorMessage'
import FormTextarea from '../components/FormTextarea'
import StoreComparison, {
  StoreComparisonItem,
} from '../components/StoreComparison'
import LoadingSpinner from '../components/LoadingSpinner'
import ReviewCategoryAddModal from '../components/ReviewCategoryAddModal'
import { toastUtil } from '../utils/toast'
import { mapApi, zoneFromScore, RankedZoneItem } from '../api/map'
import { reviewCategoryApi, type ReviewCategory } from '../api/reviewCategory'
import { reviewApi, ReviewResponse, PlaceVisitSummary } from '../api/review'
import ExistingRecordDialog from '../components/ExistingRecordDialog'
import VisitPickBottomSheet from '../components/VisitPickBottomSheet'
import ReviewPeekOverlay from '../components/ReviewPeekOverlay'
import {
  pickSkipPivot,
  useReviewStore,
  VisitDraft,
} from '../stores/reviewStore'
import { PhotoItem } from '../types/photo'
import { useCategoryStore } from '../stores/categoryIconStore'
import { VisitType, VISIT_TYPE_NAMES } from '../constants/visitType'
import BoldCheckbox from '../components/BoldCheckbox'
import { MAX_PHOTOS } from '../constants/layout'
import { FLUTTER_GALLERY_ERROR_MESSAGE } from '../constants/flutterBridgeMessages'
import { isApp } from '../utils/platform'
import NearbyPhotoSuggestions from '../components/NearbyPhotoSuggestions'
import { autoCropToAllowedAspect } from '../utils/autoCropPhoto'
import { downscaleForUpload } from '../utils/downscalePhoto'
import { preuploadPhoto } from '../utils/preuploadPhoto'
import { invalidateReviewCaches } from '../utils/homeFeedCache'
import { readPhotoTakenAt } from '../utils/photoTakenAt'
import { dataURLtoFile, fileToBase64 } from '../utils/fileUtils'
import { devDebug } from '../utils/devDebug'
import {
  reportIfScrollBlocked,
  resetScrollDiagnostics,
} from '../utils/scrollDiagnostics'
import FriendTagBottomSheet from '../components/FriendTagBottomSheet'
import Divider from '../components/Divider'
import DraggablePhotoList from '../components/DraggablePhotoList'
import { toWaitingTimeOption } from '../constants/waitingTime'
import { getPriceRangeIcon } from '../constants/priceRange'

const logStoreReviewPhoto = (
  stage: string,
  storeId: string | undefined,
  detail: Record<string, unknown>,
) => {
  devDebug.log('[StoreReview:photo]', {
    stage,
    storeId: storeId ?? null,
    ...detail,
  })
}

const buildReviewCategorySelectOptions = (
  categories: ReviewCategory[],
): SelectOption[] => {
  const options: SelectOption[] = categories.map((category) => ({
    value: category.id.toString(),
    label: category.name,
  }))
  // [우선 숨김] 카테고리 "+ 추가하기" 옵션 — 나중에 복구 시 아래 주석 해제
  // options.push({ value: '__add__', label: '+ 추가하기' })
  return options
}

const areReviewCategoryOptionsEqual = (
  a: SelectOption[],
  b: SelectOption[],
): boolean => {
  if (a.length !== b.length) {
    return false
  }

  return a.every(
    (opt, i) => opt.value === b[i].value && opt.label === b[i].label,
  )
}

const StoreReview: React.FC = () => {
  const navigate = useNavigate()
  const { storeId } = useParams<{ storeId: string }>()
  const [searchParams] = useSearchParams()
  /** 자세히보기에서 '수정하기'로 들어왔다면 고칠 기록 id가 붙어 있다 */
  const requestedReviewId = (() => {
    const raw = searchParams.get('reviewId')
    if (!raw) return null
    const n = parseInt(raw, 10)
    return Number.isFinite(n) && n > 0 ? n : null
  })()
  const getCategoryIconBrown = useCategoryStore(
    (state) => state.getCategoryIconBrown,
  )
  const getCategoryName = useCategoryStore((state) => state.getCategoryName)
  const getAllCategories = useCategoryStore((state) => state.getAllCategories)
  const {
    storeId: storeIdInStore,
    selectedCategory,
    experienceScore,
    photos,
    reviewText,
    visitDate,
    waitingTime,
    visitType,
    is_public,
    minPricePerPerson,
    maxPricePerPerson,
    taggedFriendNames,
    reviewKeywordNames,
    compareStoreIds,
    setStoreId,
    setSelectedCategory,
    setExperienceScore,
    setPhotos,
    setReviewText,
    setVisitDate,
    setWaitingTime,
    setVisitType,
    setIsPublic,
    setPricePerPerson,
    setCompareStoreIds,
    initZoneSearch,
    applyZoneComparison,
    skipZoneComparison,
    undoZoneComparison,
    searchLo,
    searchHi,
    currentPivot,
    skippedPivots,
    zoneHistory,
    taggedFriendIds,
    toggleTaggedFriend,
    setTaggedFriendIds,
    reviewKeywordIds,
    setReviewKeywordIds,
    isReturningFromSubPage,
    setIsReturningFromSubPage,
    storeInfo,
    setStoreInfo,
    resetReviewData,
    captureVisitDraft,
    applyVisitDraft,
    visitIntent,
    setVisitIntent,
    exitComparisonMode,
    reviewCategoryOptions,
    setReviewCategoryOptions,
  } = useReviewStore()

  const fetchReviewCategoriesFromApi = async () => {
    try {
      const response = await reviewCategoryApi.getCategories()
      const next = buildReviewCategorySelectOptions(response.categories)
      const prev = useReviewStore.getState().reviewCategoryOptions
      if (!areReviewCategoryOptionsEqual(prev, next)) {
        setReviewCategoryOptions(next)
      }
    } catch {
      if (useReviewStore.getState().reviewCategoryOptions.length === 0) {
        setReviewCategoryOptions([]) // [우선 숨김] "+ 추가하기" 제거
      }
    }
  }

  const [isCategoryAddModalOpen, setIsCategoryAddModalOpen] = useState(false)

  // 검증 오류 메시지 상태
  const [categoryError, setCategoryError] = useState<string>('')
  const [reviewTextError, setReviewTextError] = useState<string>('')

  // 한줄평 최대 길이
  const MAX_REVIEW_TEXT_LENGTH = 300
  const EMPTY_IMAGE_DATA_ERROR_MESSAGE =
    '이미지 데이터가 비어 있어 업로드할 수 없습니다'
  const HEIF_CONVERT_ERROR_MESSAGE = 'HEIF 이미지를 변환할 수 없습니다'

  // 비교 모드 확인
  const isCompareMode = compareStoreIds.length > 0

  // 로딩 및 에러 상태
  const [isLoadingStore, setIsLoadingStore] = useState(false)
  const [storeError, setStoreError] = useState<string | null>(null)

  /**
   * 재방문 — 이 장소에 이미 남긴 기록들.
   *
   * 하나도 없으면 빈 배열이고, 화면은 예전과 똑같이 동작한다(라벨도 팝업도
   * 안 뜬다). 있을 때만 "고칠 건지 새로 남길 건지"를 묻는다.
   */
  const [placeVisits, setPlaceVisits] = useState<PlaceVisitSummary[]>([])
  /*
   * 수정 중인 기록 / 몇 번째인지 — 스토어의 visitIntent에서 읽는다.
   *
   * 화면 상태(useState)로 두면 사진·방문일자 화면에 다녀오는 사이 사라진다.
   * 그러면 '새로 남기는 중'이던 것이 '최근 기록 수정'으로 바뀌어 원래 기록을
   * 덮어쓴다. 실제로 그렇게 기록이 하나 사라졌다.
   */
  const intent =
    visitIntent && visitIntent.placeId === storeId ? visitIntent : null
  const editingReviewId = intent?.reviewId ?? null
  const visitNumber = intent?.visitNumber ?? null
  /** 기록이 적어 점수를 가려야 하는 사용자인지 (회차 시트에 쓴다) */
  const [hideScore, setHideScore] = useState(false)
  const [showExistingDialog, setShowExistingDialog] = useState(false)
  const [showVisitPicker, setShowVisitPicker] = useState(false)

  /*
   * '기록할 사진'(Collect)에서 골라 들고 온 사진.
   *
   * 이미 기록한 가게면 화면이 기존 기록을 불러와 폼을 채운다. 예전에는 그때
   * 사진 목록을 기존 기록의 사진으로 **통째로 바꿔서** 들고 온 사진이 사라졌고,
   * '새로운 기록'을 골라도 폼을 비우면서 같이 지워졌다.
   *
   * 그래서 들고 온 사진을 따로 기억해 두고, 지금 보고 있는 회차에 붙인다.
   * - 기록 수정: 기존 사진 뒤에 붙는다.
   * - 새로운 기록: 빈 폼에 들어간다.
   * - 회차를 바꾸면: 떠나는 회차에서 빼고 새 회차로 옮긴다(한 회차에만 붙는다).
   */
  const incomingPhotosRef = useRef<PhotoItem[]>([])
  /** 들고 온 사진의 찍은 날짜. 새로운 기록일 때만 방문일자로 쓴다 */
  const incomingVisitDateRef = useRef<string | null>(null)

  const withoutIncoming = (list: PhotoItem[]): PhotoItem[] => {
    const ids = new Set(incomingPhotosRef.current.map((p) => p.id))
    return ids.size === 0 ? list : list.filter((p) => !ids.has(p.id))
  }

  const withIncoming = (list: PhotoItem[]): PhotoItem[] => {
    const incoming = incomingPhotosRef.current
    if (incoming.length === 0) return list
    const have = new Set(list.map((p) => p.id))
    const merged = [...list, ...incoming.filter((p) => !have.has(p.id))]
    if (merged.length > MAX_PHOTOS) {
      toastUtil.warning(
        `사진은 최대 ${MAX_PHOTOS}장이라 ${merged.length - MAX_PHOTOS}장은 빠졌어요`,
      )
      return merged.slice(0, MAX_PHOTOS)
    }
    return merged
  }

  const [isLoadingComparison, setIsLoadingComparison] = useState(false)
  /**
   * '다음으로'를 눌러 비교 준비가 도는 동안 true.
   *
   * 예전에는 이 구간에 아무 표시가 없었다. 랭킹 목록 조회는 최대 60초(axios 타임아웃)까지
   * 걸릴 수 있는데 그동안 화면이 미동도 안 해서, 느린 것과 버튼이 죽은 것이 구별되지
   * 않았다. 실제로 사용자가 "다음으로가 안 눌린다"고 신고한 상황이 이것이었다.
   */
  const [isStartingComparison, setIsStartingComparison] = useState(false)
  // 하위 페이지에서 돌아왔는지 추적 (플래그 초기화 후에도 체크 가능하도록)
  const [wasReturningFromSubPage, setWasReturningFromSubPage] = useState(false)

  // 비교 모드일 때 현재 가게 정보 (왼쪽)
  const [currentStore, setCurrentStore] = useState<StoreComparisonItem | null>(
    null,
  )
  // 비교 모드일 때 비교할 가게 정보 리스트 (오른쪽)
  const [compareStores, setCompareStores] = useState<StoreComparisonItem[]>([])
  // B(비교 대상) 길게 누르기 미리보기: 내 리뷰 프리페치 + 표시 상태
  const [comparePeekReview, setComparePeekReview] = useState<
    ReviewResponse['review'] | null
  >(null)
  const [isComparePeekOpen, setIsComparePeekOpen] = useState(false)
  // 비교 모드에서 선택된 가게 ID
  const [selectedComparisonStoreId, setSelectedComparisonStoreId] = useState<
    string | null
  >(null)
  // initData 실행 중 플래그
  const initDataRef = useRef(false)
  /**
   * 비교 한 판이 진행 중인지. state가 아니라 ref인 이유는, 카드 탭이 연달아
   * 들어오면 리렌더 전에 두 번째가 들어오기 때문이다. state는 그 시점에 아직
   * 옛 값이라 막지 못한다.
   */
  const isLoadingComparisonRef = useRef(false)
  /** 비교 시작(다음으로)이 진행 중인지. 위와 같은 이유로 state가 아닌 ref다. */
  const isStartingComparisonRef = useRef(false)
  /**
   * 지금 화면에 있는 비교 상대(B)의 장소 id.
   *
   * 미리보기 리뷰를 백그라운드로 받아오는데, 빠르게 두 번 건너뛰면 먼저 보낸
   * 요청이 나중에 도착할 수 있다. 그때 화면의 상대와 다른 리뷰가 붙지 않도록
   * 도착한 응답이 지금 상대의 것인지 여기서 확인한다.
   */
  const currentCompareIdRef = useRef<string | null>(null)

  // 친구 태그 바텀 시트 상태
  const [isFriendTagBottomSheetOpen, setIsFriendTagBottomSheetOpen] =
    useState(false)

  // 사진 추가 관련
  const fileInputRef = useRef<HTMLInputElement>(null)

  const isHeifFile = (file: File): boolean => {
    const lowerName = file.name.toLowerCase()
    const isHeifByName =
      lowerName.endsWith('.heic') || lowerName.endsWith('.heif')
    const heifMimeTypes = [
      'image/heic',
      'image/heif',
      'image/heic-sequence',
      'image/heif-sequence',
    ]
    const isHeifByMime = heifMimeTypes.includes((file.type || '').toLowerCase())

    return isHeifByName || isHeifByMime
  }

  const convertHeifToJpeg = async (file: File): Promise<File> => {
    const heic2any = (await import('heic2any')).default
    const converted = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.92,
    })

    const convertedBlob = Array.isArray(converted) ? converted[0] : converted
    if (!convertedBlob || convertedBlob.size === 0) {
      throw new Error(HEIF_CONVERT_ERROR_MESSAGE)
    }

    const convertedName = file.name.replace(/\.(heic|heif)$/i, '.jpg')
    return new File([convertedBlob], convertedName, {
      type: 'image/jpeg',
    })
  }

  const normalizeImageFile = async (file: File): Promise<File> => {
    if (!isHeifFile(file)) {
      return file
    }

    return convertHeifToJpeg(file)
  }

  const isSupportedImageFile = (file: File): boolean => {
    if (file.type.startsWith('image/')) {
      return true
    }

    return isHeifFile(file)
  }

  const isDecodedImageDataEmpty = (dataUrl: string): boolean => {
    if (!dataUrl.startsWith('data:image')) {
      return false
    }

    const dataParts = dataUrl.split(',')
    if (dataParts.length !== 2) {
      return true
    }

    const base64Data = dataParts[1]?.trim() || ''
    if (!base64Data) {
      return true
    }

    try {
      return atob(base64Data).length === 0
    } catch (_error) {
      return true
    }
  }

  // 초기 데이터 로딩 (컴포넌트 마운트 시 한 번만 실행)
  /**
   * 사진을 붙인 직후 화면이 스크롤되지 않는 일이 아주 가끔 있었다.
   * 재현이 안 돼 원인을 특정하지 못했으므로, 의심 상태가 성립하면
   * 그 순간의 화면 정보를 서버에 남긴다(사용자에게는 보이지 않음).
   * 레이아웃이 자리를 잡은 뒤에 봐야 하므로 두 번에 나눠 확인한다.
   */
  useEffect(() => {
    if (photos.length === 0) {
      return
    }
    const timers = [
      window.setTimeout(
        () =>
          reportIfScrollBlocked(
            'store_review:photo_added',
            '.app-container.overflow-y-auto',
          ),
        300,
      ),
      window.setTimeout(
        () =>
          reportIfScrollBlocked(
            'store_review:photo_added_settled',
            '.app-container.overflow-y-auto',
          ),
        1500,
      ),
    ]
    return () => timers.forEach((t) => window.clearTimeout(t))
  }, [photos.length])

  useEffect(() => resetScrollDiagnostics, [])

  useEffect(() => {
    if (!storeId) {
      return
    }

    // AB 중 뒤로가기 후 다른 가게로 진입 시 compareStoreIds만 남으면 isCompareMode인데
    // 로컬 compareStores는 비어 하단(다음으로/비교 카드)이 비는 문제 방지
    exitComparisonMode()
    setCurrentStore(null)
    setCompareStores([])
    setSelectedComparisonStoreId(null)

    // 하위 페이지에서 돌아온 경우 서버 데이터 로드 스킵
    // 전역 스토어(reviewStore)에 저장된 값들(photos, visitDate, waitingTime 등)을 그대로 사용
    if (isReturningFromSubPage) {
      const snap = useReviewStore.getState()
      logStoreReviewPhoto('page_mount', storeId, {
        branch: 'subpage_return_skip_init',
        photosCount: snap.photos.length,
        photos: snap.photos.map((p) => ({
          id: p.id,
          preview: devDebug.dataUrlMeta(p.preview),
        })),
      })
      setWasReturningFromSubPage(true) // 로컬 상태에 저장 (플래그 초기화 전)
      setIsReturningFromSubPage(false) // 플래그 초기화
      void fetchReviewCategoriesFromApi()
      return
    }

    logStoreReviewPhoto('page_mount', storeId, {
      branch: 'full_init',
      photosCountBefore: useReviewStore.getState().photos.length,
    })

    // 초기화
    initDataRef.current = false
    setStoreInfo(null)
    setStoreError(null)

    /*
     * 가게가 바뀌면 전역 상태 초기화.
     *
     * 단, 가게 자리가 **비어 있었다면** 지우지 않는다. 비었다는 건 다른
     * 가게의 작성 중 내용이 남아 있는 상태가 아니라는 뜻이라 지울 것이 없고,
     * 오히려 가게를 정하기 전에 미리 심어 둔 것을 날린다 —
     * '기록할 사진'에서 직접 찾기로 들어오는 길이 그렇다. 사진만 먼저 심고
     * 가게는 검색에서 고르는데, 여기서 비우면 사진이 사라진다(Collect.tsx
     * startSearch 참고).
     */
    if (storeIdInStore !== null && storeId !== storeIdInStore) {
      resetReviewData()
    }
    if (storeId !== storeIdInStore) {
      setStoreId(storeId)
    }

    // 앞 화면('기록할 사진')이 심어 둔 사진을 기억한다. 기기에서 온 사진만
    // file 을 가진다 — 서버에 이미 있는 사진은 file 이 null 이다.
    {
      const staged = useReviewStore.getState()
      incomingPhotosRef.current = staged.photos.filter((p) => p.file)
      incomingVisitDateRef.current =
        staged.visitDateSource === 'photo' ? staged.visitDate : null
    }

    // 데이터 로드 (!isCompareMode 조건 제거: 위에서 비교 잔여는 이미 제거함)
    initData(storeId)

    return () => {
      exitComparisonMode()

      /*
        화면을 떠나면 '무엇을 하는 중이었는지'를 지운다.

        하위 화면(사진·방문일자·키워드)으로 갈 때는 지우면 안 된다 — 돌아와서
        이어 써야 한다. 그때는 넘어가기 직전에 isReturningFromSubPage가 켜져
        있으므로 그것으로 구분한다.

        안 지우면 한 번 들어갔던 가게는 의도가 계속 남아, 다음에 다시 들어가도
        '수정 중'으로 기억돼 **팝업이 아예 안 뜬다**(= 새로운 기록을 만들 수
        없다). 실제로 그렇게 막혔다.
      */
      if (!useReviewStore.getState().isReturningFromSubPage) {
        setVisitIntent(null)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const initData = async (storeId: string) => {
    if (initDataRef.current) {
      return
    }

    initDataRef.current = true
    setIsLoadingStore(true)
    setStoreError(null)

    try {
      // 가게 정보, 기존 리뷰 — 카테고리는 전역 스토어 목록을 먼저 쓰고 API로 갱신
      const [placeData, existingReview] = await Promise.allSettled([
        mapApi.getPlace(storeId),
        // reviewId가 붙어 있으면 그 회차를 연다 — 자세히보기에서 '수정하기'로
        // 들어온 경우다. 없으면 가장 최근 회차.
        reviewApi
          .getReviewByPlace(parseInt(storeId), requestedReviewId ?? undefined)
          .catch(() => null), // 기존 리뷰가 없으면 null
      ])

      // 가게 정보는 무조건 성공해야 함
      if (placeData.status === 'rejected') {
        setStoreError('가게 정보를 불러오는데 실패했습니다')
        setIsLoadingStore(false)
        initDataRef.current = false
        return
      }

      // 가게 정보 설정
      const allCategories = getAllCategories()
      const defaultCategoryCode =
        allCategories.length > 0 ? allCategories[0].code : null
      const categoryCode = placeData.value.category || defaultCategoryCode || ''
      setStoreInfo({
        id: storeId,
        name: placeData.value.name,
        category: getCategoryName(categoryCode),
        categoryCode,
        address: placeData.value.address,
        lat: placeData.value.lat ?? null,
        lng: placeData.value.lng ?? null,
        // 랭킹 비교에서 '같은 종류'를 먼저 붙이는 데 쓴다
        subCategory: placeData.value.sub_category?.trim() || null,
      })

      await fetchReviewCategoriesFromApi()

      const options = useReviewStore.getState().reviewCategoryOptions

      // 신규 리뷰 기본값(자동 추천): 사용자가 매번 고르는 수고를 줄인다.
      // (기존 리뷰가 있으면 아래에서 이전에 고른 카테고리로 덮어씀)
      // 매칭 우선순위:
      //  1) 세부 라벨("한식" 등)과 같은 이름의 리뷰 카테고리 (사용자가 그렇게 만든 경우)
      //  2) 가게 메인 카테고리(restaurant/cafe/bar)에 대응하는 대표 이름
      // ※ 음식점은 kakao 세부 라벨이 한식/양식/분식 등 cuisine이라 "레스토랑"과 안 맞음 →
      //   메인 카테고리 매핑이 있어야 자동선택됨.
      const placeSubLabel = placeData.value.sub_category?.trim()
      const mainCategoryNames: Record<string, string[]> = {
        restaurant: ['레스토랑', '음식점', '맛집', '식당'],
        cafe: ['카페'],
        bar: ['술집', '주점', '바'],
      }
      const candidateNames = [
        ...(placeSubLabel ? [placeSubLabel] : []),
        ...(mainCategoryNames[placeData.value.category] ?? []),
      ]
      const matched = candidateNames
        .map((name) => options.find((opt) => opt.label === name))
        .find((opt): opt is SelectOption => Boolean(opt))
      if (matched) {
        setSelectedCategory(matched)
      }

      // 기존 리뷰가 있으면 수정 모드로 진입
      if (existingReview.status === 'fulfilled' && existingReview.value) {
        const review = existingReview.value.review

        // 재방문 — 회차를 기억해 두고, 무엇을 할지 물어본다.
        //
        // 폼은 아래에서 최신 회차 내용으로 채워진다. '새로운 기록'을 고르면
        // 그때 비운다. 먼저 채워두는 쪽이 '기록 수정'(대부분의 경우)에서
        // 기다림 없이 바로 보이기 때문이다.
        const visits = existingReview.value.visits ?? []
        setPlaceVisits(visits)
        setHideScore(existingReview.value.hide_score ?? false)

        /*
          이미 정해둔 게 있으면 그대로 이어간다.

          사진·방문일자 화면에 다녀오면 이 함수가 다시 돈다. 그때 무조건
          '최근 기록 수정'으로 덮어써 버리면, 새로 남기던 중이던 사람이
          돌아왔을 때 원래 기록을 고치는 상태가 된다.
        */
        const kept =
          useReviewStore.getState().visitIntent?.placeId === storeId
            ? useReviewStore.getState().visitIntent
            : null

        if (kept) {
          // 새로 남기는 중이면 회차 번호만 최신으로 맞춰준다
          // (그 사이 다른 기기에서 회차가 늘었을 수 있다)
          if (kept.mode === 'new') {
            const nextNumber =
              visits.reduce((m, v) => Math.max(m, v.visit_number), 0) + 1
            if (kept.visitNumber !== nextNumber) {
              setVisitIntent({ ...kept, visitNumber: nextNumber })
            }
          }
        } else {
          setVisitIntent({
            placeId: storeId,
            mode: 'edit',
            reviewId: review.id,
            visitNumber: review.visit_number ?? visits[0]?.visit_number ?? 1,
          })

          /*
            무엇을 할지 물어본다 — 단, 물어볼 게 있을 때만.

            - 고칠 기록을 정하고 들어왔으면(자세히보기의 '수정하기') 묻지 않는다.
            - 기록이 상한까지 찼으면 '새로운 기록'을 만들 수 없으므로 고를 것이
              없다. 버튼 하나짜리 팝업은 한 번 더 누르게 할 뿐이라 띄우지 않고
              바로 수정 화면으로 연다. (회차는 제목 위 ▾로 바꾼다)
          */
          const maxAllowed = existingReview.value.max_visits ?? null
          const isFull = maxAllowed !== null && visits.length >= maxAllowed

          if (visits.length > 0 && requestedReviewId === null && !isFull) {
            setShowExistingDialog(true)
          }
        }

        applyReviewToForm(review, options)
        // 화면에 처음 올라온 회차도 '다 읽어온' 것으로 표시한다
        viewingVisitRef.current = review.id
        loadedVisitsRef.current.add(review.id)
      }
    } catch (error) {
      console.error('초기 데이터 가져오기 실패:', error)
      setStoreError('가게 정보를 불러오는데 실패했습니다')

      if (useReviewStore.getState().reviewCategoryOptions.length === 0) {
        setReviewCategoryOptions([]) // [우선 숨김] "+ 추가하기" 제거
      }
    } finally {
      setIsLoadingStore(false)
      initDataRef.current = false
    }
  }

  /**
   * 서버에서 받은 기록 하나로 폼을 채운다.
   *
   * 화면에 들어올 때와, 회차를 바꿀 때 같은 코드를 쓴다. 예전에는 진입
   * 경로가 하나뿐이라 initData 안에 펼쳐 두었는데, 이제 두 곳에서 필요하다.
   */
  const applyReviewToForm = (
    review: ReviewResponse['review'],
    options: SelectOption[],
  ) => {
    // 카테고리 선택 (본인이 선택한 카테고리)
    if (review.category) {
      const categoryOption = options.find(
        (opt) => opt.value === review.category!.id.toString(),
      )
      if (categoryOption) {
        setSelectedCategory(categoryOption)
      }
    }

    // experienceScore는 기존 리뷰의 score를 사용하거나, internal_score를 역산
    // internal_score가 있으면 그것을 사용, 없으면 score 사용
    if (review.internal_score !== null && review.internal_score !== undefined) {
      // internal_score는 experienceScore ± 2 범위에서 시작하므로
      // 역산: experienceScore ≈ internal_score (대략적으로)
      // 하지만 정확한 역산은 불가능하므로 score를 사용하거나 기본값 사용
      setExperienceScore(
        review.score !== null && review.score !== undefined ? review.score : 5,
      )
    } else if (review.score !== null && review.score !== undefined) {
      setExperienceScore(review.score)
    }

    // 사진 설정
    if (review.photos && review.photos.length > 0) {
      // 서버에서 받아온 photos를 PhotoItem 형식으로 변환
      const photoItems: PhotoItem[] = review.photos
        .filter((photo) => {
          // blob URL인 경우 필터링 (만료된 blob URL은 사용할 수 없음)
          if (photo.preview && photo.preview.startsWith('blob:')) {
            return false
          }
          return true
        })
        .map((photo) => ({
          id: photo.id,
          file: null, // 서버에서 받아온 사진은 File 객체가 없음
          preview: photo.preview,
          description: photo.description || '',
        }))

      // 기록 사진 뒤에 '기록할 사진'에서 들고 온 사진을 붙인다.
      // 기록 사진이 하나도 없으면 지금 photos를 유지한다 (사용자가 추가한 사진 보존)
      const base =
        photoItems.length > 0
          ? photoItems
          : withoutIncoming(useReviewStore.getState().photos)
      setPhotos(withIncoming(base))
    }

    // 한줄평 설정
    if (review.review_text) {
      setReviewText(review.review_text)
    }

    // 방문일자 설정
    if (review.visit_date) {
      setVisitDate(review.visit_date)
    }

    // 웨이팅 시간 설정.
    // 예전 단위로 저장된 값(210분 등)은 '2시간 이상'으로 묶여 돌아온다.
    if (review.waiting_time !== null && review.waiting_time !== undefined) {
      setWaitingTime(toWaitingTimeOption(review.waiting_time))
    } else {
      setWaitingTime(null)
    }

    // 방문 타입 설정
    if (review.visit_type) {
      // 백엔드에서 받은 visit_type 문자열을 VisitType enum으로 변환
      const visitTypeValue =
        review.visit_type === VisitType.TAKEOUT
          ? VisitType.TAKEOUT
          : VisitType.STORE
      setVisitType(visitTypeValue)
    }

    // 공개 여부 설정
    if (review.is_public !== undefined) {
      setIsPublic(review.is_public)
    }

    // 가격대 설정
    if (
      review.min_price_per_person !== undefined ||
      review.max_price_per_person !== undefined
    ) {
      setPricePerPerson(
        review.min_price_per_person ?? null,
        review.max_price_per_person ?? null,
      )
    }

    // 친구 태그 설정
    if (review.tagged_friends && Array.isArray(review.tagged_friends)) {
      const friendIds = review.tagged_friends.map((friend: any) =>
        friend.id.toString(),
      )
      const friendNames = review.tagged_friends.map(
        (friend: any) => friend.name ?? friend.nickname ?? '',
      )
      setTaggedFriendIds(friendIds, friendNames)
    } else {
      setTaggedFriendIds([])
    }

    // 리뷰 키워드 설정
    if (review.keywords && Array.isArray(review.keywords)) {
      const keywordIds = review.keywords.map((keyword: any) => keyword.id)
      const keywordNames = review.keywords.map(
        (keyword: any) => keyword.name ?? '',
      )
      setReviewKeywordIds(keywordIds, keywordNames)
    } else {
      setReviewKeywordIds([])
    }

    // 비교 히스토리는 수정 모드에서 복원하지 않음 (새로 시작)
  }

  /**
   * 회차별로 쓰다 만 내용. 키는 기록 id, 새 회차는 'new'.
   *
   * 화면을 나가면 사라진다 — 영구 임시저장은 별개 작업이다. 여기서 막으려는
   * 건 "1번째를 보려고 잠깐 눌렀다가 쓰던 걸 통째로 잃는" 경우뿐이다.
   */
  const visitDraftsRef = useRef<Map<number | 'new', VisitDraft>>(new Map())

  /**
   * 지금 화면이 보여주는 회차. 늦게 온 응답을 버리는 데 쓴다.
   *
   * 회차를 고르면 그 내용을 서버에서 받아오는데, 응답이 오기 전에 다른 회차로
   * 옮기면 **늦게 도착한 응답이 지금 보고 있는 회차 위에 덮인다.** 그 상태로
   * 또 옮기면 잘못된 내용이 그대로 임시저장에 굳고, 저장하면 그대로 들어간다.
   * 사진이 엉뚱한 회차에 붙는 일이 실제로 났다.
   *
   * state가 아니라 ref인 이유: 비동기 함수 안에서 '지금' 값을 읽어야 한다.
   */
  const viewingVisitRef = useRef<number | 'new' | null>(null)

  /**
   * 내용을 다 읽어온 회차들. 아직 받아오는 중인 회차는 임시저장하지 않는다.
   *
   * 받아오는 도중에 떠두면 빈 폼이 그 회차의 '쓰던 내용'으로 저장돼, 나중에
   * 돌아왔을 때 내용이 통째로 비어 보인다.
   */
  const loadedVisitsRef = useRef<Set<number | 'new'>>(new Set())

  /** 이 장소에 남긴 기록 중 가장 큰 회차 번호 */
  const maxVisitNumber = placeVisits.reduce(
    (max, v) => Math.max(max, v.visit_number),
    0,
  )

  const handleEditExisting = () => setShowExistingDialog(false)

  const handleStartNewVisit = () => {
    setShowExistingDialog(false)
    // 지금 폼에는 최신 회차 내용이 채워져 있다. 새로 쓰는 것이므로 비운다.
    // 들고 온 사진은 떠나는 회차에 남기지 않는다 — 새 기록으로 옮겨 간다
    const captured = captureVisitDraft()
    const current = { ...captured, photos: withoutIncoming(captured.photos) }
    visitDraftsRef.current.set(editingReviewId ?? 'new', current)

    // 분류만 이어받는다.
    //
    // 분류는 '이번 방문'이 아니라 '이 장소'의 성격이다(레스토랑/카페/술집).
    // 비워두면 매번 다시 고르게 되고, 지난번과 다르게 고르면 같은 가게가
    // 다른 랭킹 사다리로 옮겨간다. 물론 바꾸고 싶으면 바꿀 수 있다.
    //
    // 나머지(한줄평·사진·점수·방문일자·키워드·친구·가격)는 방문마다 다르므로
    // 이어받지 않는다.
    const kept: Partial<VisitDraft> = visitDraftsRef.current.get('new') ?? {
      selectedCategory: current.selectedCategory,
    }
    const photoDate = incomingVisitDateRef.current
    applyVisitDraft({
      ...kept,
      photos: withIncoming(kept.photos ?? []),
      // 새 기록의 방문일자는 들고 온 사진의 찍은 날짜로 (정해 둔 게 없을 때만)
      ...(photoDate && !kept.visitDate
        ? { visitDate: photoDate, visitDateSource: 'photo' as const }
        : {}),
    })
    viewingVisitRef.current = 'new'
    loadedVisitsRef.current.add('new')
    setVisitIntent({
      placeId: storeId!,
      mode: 'new',
      reviewId: null,
      visitNumber: maxVisitNumber + 1,
    })
  }

  const handlePickVisit = async (visit: PlaceVisitSummary) => {
    setShowVisitPicker(false)
    if (visit.id === editingReviewId) return

    // 떠나는 회차의 내용을 떠둔다 — 단, 다 읽어온 회차만.
    // 아직 받아오는 중이었으면 빈 폼이 저장돼 내용이 사라진다.
    const leaving = editingReviewId ?? 'new'
    if (loadedVisitsRef.current.has(leaving)) {
      // 들고 온 사진은 떠나는 회차에서 빼고 고른 회차로 옮긴다
      const captured = captureVisitDraft()
      visitDraftsRef.current.set(leaving, {
        ...captured,
        photos: withoutIncoming(captured.photos),
      })
    }

    viewingVisitRef.current = visit.id

    const cached = visitDraftsRef.current.get(visit.id)
    setVisitIntent({
      placeId: storeId!,
      mode: 'edit',
      reviewId: visit.id,
      visitNumber: visit.visit_number,
    })

    if (cached) {
      applyVisitDraft({ ...cached, photos: withIncoming(cached.photos ?? []) })
      return
    }

    // 처음 여는 회차는 서버에서 받아온다
    // 이전 회차 내용이 잠깐 비치지 않게 먼저 비운다. 들고 온 사진은 바로 보인다.
    applyVisitDraft({ photos: withIncoming([]) })
    try {
      const res = await reviewApi.getReviewByPlace(parseInt(storeId!), visit.id)

      // 기다리는 사이에 다른 회차로 옮겼으면 이 응답은 버린다.
      // 안 버리면 지금 보고 있는 회차에 남의 내용이 덮인다.
      if (viewingVisitRef.current !== visit.id) return

      if (res?.review) {
        applyReviewToForm(
          res.review,
          useReviewStore.getState().reviewCategoryOptions,
        )
      }
      loadedVisitsRef.current.add(visit.id)
    } catch {
      if (viewingVisitRef.current !== visit.id) return
      toastUtil.error('기록을 불러오지 못했어요')
    }
  }

  /**
   * 비교 상대(B)만 그 자리에서 바꾼다. 요청도, 스피너도 없다.
   *
   * 왜 필요한가
   * ----------
   * loadComparisonStores는 A와 B를 둘 다 서버에서 다시 받아오고 그동안
   * 화면 전체를 스피너로 덮는다. 건너뛰기·뒤로가기는 A가 바뀌지 않는데도
   * 매번 화면이 통째로 사라졌다 나타나서 깜빡였다.
   *
   * 그럴 필요가 없다. 존 랭킹 목록(rankedList)에 이미 이름·주소·점수가 다
   * 들어 있다 — 카드를 그리는 데 필요한 건 그게 전부다. 그래서 요청 없이
   * B 카드만 갈아끼운다.
   *
   * 미리보기(꾹 누르면 나오는 내 리뷰)만 뒤에서 받아온다. 화면을 막지 않고,
   * 늦게 도착한 응답이 이미 바뀐 상대에 붙지 않도록 id를 확인한다.
   */
  const swapComparisonTarget = (item: RankedZoneItem) => {
    const id = item.placeId.toString()
    currentCompareIdRef.current = id

    setSelectedComparisonStoreId(null)
    setIsComparePeekOpen(false)
    setComparePeekReview(null)
    setCompareStoreIds([id])
    setCompareStores([
      {
        id,
        name: item.name,
        address: item.address || '',
        score: item.score || undefined,
      },
    ])

    reviewApi
      .getReviewByPlace(item.placeId)
      .then((res) => {
        if (currentCompareIdRef.current !== id) return
        setComparePeekReview(res?.review ?? null)
      })
      .catch(() => {
        if (currentCompareIdRef.current !== id) return
        setComparePeekReview(null)
      })
  }

  // 비교 가게 정보 로딩 함수
  const loadComparisonStores = async (compareStoreId: string) => {
    if (!storeId) return
    currentCompareIdRef.current = compareStoreId

    setIsLoadingComparison(true)
    setSelectedComparisonStoreId(null)
    setComparePeekReview(null)
    setIsComparePeekOpen(false)

    try {
      const [currentPlace, comparePlace] = await Promise.all([
        mapApi.getPlace(storeId),
        mapApi.getPlace(compareStoreId),
      ])

      // 현재 가게 정보 (왼쪽)
      setCurrentStore({
        id: currentPlace.id.toString(),
        name: currentPlace.name,
        address: currentPlace.address || '',
      })

      // 비교할 가게 정보 (오른쪽)
      setCompareStores([
        {
          id: comparePlace.id.toString(),
          name: comparePlace.name,
          address: comparePlace.address || '',
          score: comparePlace.score ? comparePlace.score : undefined,
        },
      ])

      // B 장소의 내 리뷰 프리페치 → 길게 누르면 즉시 미리보기
      // (늦게 도착한 응답이 이미 바뀐 상대에 붙지 않게 id를 확인한다)
      reviewApi
        .getReviewByPlace(comparePlace.id)
        .then((res) => {
          if (currentCompareIdRef.current !== compareStoreId) return
          setComparePeekReview(res?.review ?? null)
        })
        .catch(() => {
          if (currentCompareIdRef.current !== compareStoreId) return
          setComparePeekReview(null)
        })
    } catch (error) {
      console.error('비교 가게 정보 가져오기 실패:', error)
      toastUtil.error('비교 가게 정보를 불러오는데 실패했습니다')
    } finally {
      setIsLoadingComparison(false)
    }
  }

  const handleCategoryChange = (selected: SelectOption | null) => {
    if (selected && selected.value === '__add__') {
      // "+ 추가하기" 선택 시 모달 열기
      setIsCategoryAddModalOpen(true)
      setSelectedCategory(null) // 선택 해제
    } else {
      setSelectedCategory(selected)
      // 카테고리 선택 시 오류 메시지 초기화
      if (selected) {
        setCategoryError('')
      }
    }
  }

  const handleCategoryAddSuccess = (category: {
    id: number
    name: string
    created_at: string
  }) => {
    // 새로 추가된 카테고리를 옵션에 추가
    const newOption: SelectOption = {
      value: category.id.toString(),
      label: category.name,
    }
    // "+ 추가하기" 옵션을 제거하고 새 카테고리 추가 후 다시 추가
    const currentOptions = reviewCategoryOptions.filter(
      (opt) => opt.value !== '__add__',
    )
    setReviewCategoryOptions([
      ...currentOptions,
      newOption,
      { value: '__add__', label: '+ 추가하기' },
    ])
    // 새로 추가된 카테고리 선택
    setSelectedCategory(newOption)
  }

  const handleCategoryAddModalClose = () => {
    setIsCategoryAddModalOpen(false)
  }

  const handleNext = async () => {
    // 이미 진행 중이면 무시한다.
    // SubmitButton은 loading일 때 onDisabled(=handleNext)를 부르므로,
    // 막지 않으면 스피너가 도는 동안 누른 만큼 요청이 중복으로 나간다.
    //
    // state가 아니라 ref로 막는다. 연타는 리렌더 전에 들어오므로 그 시점의
    // isStartingComparison은 아직 false다. 실제로 이게 뚫려서, initZoneSearch가
    // 두 번 돌아 첫 비교 상대(무작위 pivot)가 둘 생겼다. 화면은 늦게 도착한 쪽이
    // 차지하고 탐색 상태는 다른 쪽을 기억하니, 화면에 보인 가게가 탐색 범위에서
    // 빠지지 않아 나중에 같은 가게가 또 나왔다. (13곳 목록으로 288가지 조합 확인)
    if (isStartingComparisonRef.current) {
      return
    }
    isStartingComparisonRef.current = true
    try {
      await runNextValidationAndStart()
    } finally {
      // 검증 실패로 중간에 빠져나가도 반드시 푼다. 안 그러면 버튼이 영영 죽는다.
      isStartingComparisonRef.current = false
    }
  }

  /** handleNext의 실제 본문. 조기 반환이 많아 가드 해제는 호출부의 finally가 맡는다. */
  const runNextValidationAndStart = async () => {
    if (!storeId) {
      toastUtil.error('가게 정보를 불러오는데 실패했습니다')
      return
    }

    // 검증 오류 메시지 초기화
    setCategoryError('')
    setReviewTextError('')

    let firstError: string | null = null

    // 카테고리 선택 검증 (필수)
    if (!selectedCategory) {
      const errorMsg = '카테고리를 선택해주세요'
      setCategoryError(errorMsg)
      if (!firstError) {
        firstError = errorMsg
      }
    }

    // 이용 경험(평가) 선택 검증 (필수) — 다신 안가/아쉬워/좋아/인생 맛집 중
    // 하나를 고르기 전(experienceScore < 0)에는 다음으로 진행 불가.
    if (experienceScore < 0) {
      const errorMsg = '이용 경험을 선택해주세요'
      if (!firstError) {
        firstError = errorMsg
      }
    }

    // 한줄평 최대 길이 검증 (선택사항이지만 길이 제한은 있음)
    if (reviewText && reviewText.length > MAX_REVIEW_TEXT_LENGTH) {
      const errorMsg = `한줄평은 최대 ${MAX_REVIEW_TEXT_LENGTH}자까지 작성할 수 있습니다.`
      setReviewTextError(errorMsg)
      if (!firstError) {
        firstError = errorMsg
      }
    }

    if (firstError) {
      toastUtil.error(firstError)
      return
    }

    // K=3 존+순위: 슬라이더 값으로 존 결정 후, 그 존의 내 랭킹 리스트를 받아 이진탐색
    await startZoneComparison()
  }

  // 존 비교 시작: 내 (카테고리, 존) 랭킹 리스트 조회 → 첫 비교 대상 로드 (없으면 비교 없이 저장)
  const startZoneComparison = async () => {
    // 조용히 끝내지 않는다.
    //
    // 예전에는 아래 세 조건이 걸리면 아무 말 없이 return했다. 검증(handleNext)은
    // 통과했는데 여기서 멈추니, 사용자에게는 버튼이 죽은 것으로만 보였다.
    // 이유를 알려주고, 왜 어긋났는지 추적할 수 있게 로그도 남긴다.
    if (!storeId) {
      toastUtil.error('가게 정보를 불러오는데 실패했습니다')
      return
    }

    // 검증은 화면이 구독한 값(selectedCategory)을, 실행은 스토어의 최신값을 봤다.
    // 둘이 어긋나면 버튼은 켜져 있는데 아무 일도 안 일어난다. 같은 값을 쓴다.
    const sc = selectedCategory
    const reviewCategoryId = sc?.value ? parseInt(sc.value, 10) : NaN
    if (
      !sc?.value ||
      sc.value === '__add__' ||
      Number.isNaN(reviewCategoryId)
    ) {
      console.error('[StoreReview] 카테고리 값이 올바르지 않음', {
        selected: sc,
        fromStore: useReviewStore.getState().selectedCategory,
      })
      toastUtil.error('카테고리를 다시 선택해주세요')
      return
    }

    const zone = zoneFromScore(experienceScore)
    const placeIdNum = parseInt(storeId, 10)
    setIsStartingComparison(true)
    try {
      const resp = await mapApi.getRankedZoneList({
        placeId: placeIdNum,
        reviewCategoryId,
        zone,
        excludePlaceId: placeIdNum, // 자기 자신 제외 (수정 시 포함)
      })
      // 지금 기록 중인 가게의 종류(일식·회 …). 같은 종류를 첫 비교 상대로 우선한다.
      // 카테고리별로 최근 첫 상대를 따로 기억한다 (레스토랑과 카페는 다른 목록이다)
      const firstItem = initZoneSearch(
        zone,
        resp.items,
        storeInfo?.subCategory ?? null,
        reviewCategoryId ?? null,
      )
      if (firstItem) {
        setCompareStoreIds([firstItem.placeId.toString()])
        await loadComparisonStores(firstItem.placeId.toString())
      } else {
        // 같은 존에 비교할 장소가 없음 → 비교 없이 저장 (insertIndex=0)
        await handleComparisonComplete()
      }
    } catch (error) {
      console.error('존 비교 시작 실패:', error)
      // 실패 시 비교 없이 저장 (빈 리스트로 초기화 → insertIndex=0)
      initZoneSearch(zone, [])
      await handleComparisonComplete()
    } finally {
      setIsStartingComparison(false)
    }
  }

  const handleAddPhotos = () => {
    if (photos.length >= MAX_PHOTOS) {
      toastUtil.warning(`최대 ${MAX_PHOTOS}장까지 추가할 수 있습니다`)
      return
    }

    // 앱 환경이면 Flutter 브리지 사용 (pickMultiImage)
    if (isApp() && (window as any).FlutterBridge) {
      const flutterBridge = (window as any).FlutterBridge

      flutterBridge.onGalleryError = (data: { message?: string }) => {
        logStoreReviewPhoto('flutter_gallery_error', storeId, {
          message: data?.message ?? null,
        })
        toastUtil.error(data?.message ?? FLUTTER_GALLERY_ERROR_MESSAGE)
      }

      flutterBridge.onImagesSelected = async (data: any) => {
        logStoreReviewPhoto('flutter_images_selected', storeId, {
          hasPayload: Boolean(data?.images?.length),
          selectedCount: Array.isArray(data?.images) ? data.images.length : 0,
        })

        if (
          !data ||
          !data.images ||
          !Array.isArray(data.images) ||
          data.images.length === 0
        ) {
          logStoreReviewPhoto('flutter_images_ignored', storeId, {
            reason: 'empty_or_invalid_payload',
          })
          return
        }

        const {
          photos: currentPhotos,
          setPhotos,
          setIsReturningFromSubPage,
        } = useReviewStore.getState()
        const remaining = MAX_PHOTOS - currentPhotos.length
        if (remaining <= 0) {
          logStoreReviewPhoto('flutter_images_ignored', storeId, {
            reason: 'already_max_photos',
            max: MAX_PHOTOS,
          })
          return
        }

        // 1장만 선택 시 기존처럼 크롭 편집 화면으로 이동
        if (data.images.length === 1) {
          const img = data.images[0]
          const rawFile = dataURLtoFile(img.data, img.name || `image-0.jpg`)
          let file: File
          try {
            file = await normalizeImageFile(rawFile)
          } catch (_error) {
            toastUtil.error(HEIF_CONVERT_ERROR_MESSAGE)
            return
          }

          // 변환을 거치지 않은 원본에서 읽는다. heic2any 로 바꾼 결과에는
          // EXIF가 남지 않는다.
          await fillVisitDateFromPhoto(rawFile)
          const preview = file === rawFile ? img.data : await fileToBase64(file)
          logStoreReviewPhoto('navigate_photo_edit', storeId, {
            rawFileType: rawFile.type,
            normalizedFileType: file.type,
            preview: devDebug.dataUrlMeta(preview),
          })
          setIsReturningFromSubPage(true)
          navigate(`/photo-edit/${storeId}`, {
            state: { file, preview },
          })
          return
        }

        const slice = data.images.slice(0, remaining)
        const newItems: PhotoItem[] = []
        for (let index = 0; index < slice.length; index++) {
          const img = slice[index]
          const rawFile = dataURLtoFile(
            img.data,
            img.name || `image-${index}.jpg`,
          )
          let file: File
          try {
            file = await normalizeImageFile(rawFile)
          } catch (_error) {
            toastUtil.error(HEIF_CONVERT_ERROR_MESSAGE)
            continue
          }

          const rawPreview =
            file === rawFile ? img.data : await fileToBase64(file)
          // 촬영일은 캔버스를 거치기 전에, 변환을 거치지 않은 원본에서 읽는다.
          // autoCrop 이 다시 그리는 순간 EXIF가 사라진다.
          await fillVisitDateFromPhoto(rawFile)
          // 여러 장은 크롭 화면을 건너뛰므로 여기서 허용 비율로 맞춘다.
          // 이미 1:1 · 4:5 · 5:4면 손대지 않는다.
          const fitted = await autoCropToAllowedAspect(file, rawPreview)
          newItems.push({
            id: `${Date.now()}-${index}-${Math.random()}`,
            file: fitted.file,
            preview: fitted.preview,
            description: '',
            // 원본을 남겨둬야 나중에 다시 편집할 수 있다.
            originalPreview: rawPreview,
            originalFile: file,
          })
        }

        if (newItems.length === 0) {
          logStoreReviewPhoto('flutter_multi_add_skipped', storeId, {
            reason: 'all_items_failed_normalize',
            attempted: slice.length,
          })
          return
        }
        setPhotos([...currentPhotos, ...newItems])
        // 저장을 기다리게 하지 않으려고 지금부터 올린다 (await 하지 않는다)
        newItems.forEach((it) => void preuploadPhoto(it.id, it.preview))
        logStoreReviewPhoto('flutter_multi_added', storeId, {
          addedCount: newItems.length,
          beforeCount: currentPhotos.length,
          afterCount: currentPhotos.length + newItems.length,
          truncated: data.images.length > remaining,
        })

        if (data.images.length > remaining) {
          toastUtil.warning(
            `최대 ${MAX_PHOTOS}장까지 추가 가능해 일부만 추가되었습니다`,
          )
        }
      }

      logStoreReviewPhoto('flutter_open_gallery', storeId, {})
      // 남은 장수를 앱에 알려 고르는 단계에서 막는다.
      // 예전엔 몇 장이든 고르게 두고 앱이 전부 읽어 넘긴 뒤 여기서 잘랐다.
      // 버릴 사진까지 원본으로 읽어 base64로 부풀리다 보니 메모리가 터졌다.
      flutterBridge.openGallery({ remaining: MAX_PHOTOS - photos.length })
    } else {
      logStoreReviewPhoto('web_open_file_input', storeId, {})
      fileInputRef.current?.click()
    }
  }

  // 위치 기반 추천 사진 탭 → 갤러리 1장 선택과 동일하게 크롭 편집 화면으로
  /**
   * 사진의 촬영일(ISO)을 방문일자 형식('YYYY-MM-DD HH:mm')으로 바꾼다.
   * 앱이 기기 시간대의 DateTime을 그대로 보내므로 현지 시각으로 읽으면 된다.
   */
  const toVisitDate = (iso?: string): string | null => {
    if (!iso) return null
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    const p2 = (n: number) => String(n).padStart(2, '0')
    return (
      `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())} ` +
      `${p2(d.getHours())}:${p2(d.getMinutes())}`
    )
  }

  /**
   * 사진에서 촬영일을 읽어 방문일자를 채운다.
   *
   * 반드시 캔버스로 다시 그리기 전에 불러야 한다. autoCropToAllowedAspect 와
   * downscaleForUpload 는 캔버스를 쓰므로 그 시점에 EXIF가 사라진다.
   * 이미 정해진 값이 있으면 스토어가 알아서 무시한다.
   */
  const fillVisitDateFromPhoto = async (file: File) => {
    const takenAt = await readPhotoTakenAt(file)
    if (takenAt) {
      useReviewStore.getState().setVisitDateFromPhoto(takenAt)
    }
  }

  const handleNearbyPhotoPick = (
    file: File,
    preview: string,
    takenAt?: string,
  ) => {
    // 방문일자를 아직 아무도 안 정했으면 사진 촬영일로 채운다.
    // 사용자가 직접 고른 값이나 먼저 넣은 사진이 채운 값은 건드리지 않는다.
    const fromPhoto = toVisitDate(takenAt)
    if (fromPhoto) {
      useReviewStore.getState().setVisitDateFromPhoto(fromPhoto)
    }

    if (photos.length >= MAX_PHOTOS) {
      toastUtil.warning(`최대 ${MAX_PHOTOS}장까지 추가할 수 있습니다`)
      return
    }
    logStoreReviewPhoto('nearby_photo_picked', storeId, {
      fileType: file.type,
    })
    setIsReturningFromSubPage(true)
    navigate(`/photo-edit/${storeId}`, {
      state: { file, preview },
    })
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const selectedFiles = Array.from(files)
    const imageFiles = selectedFiles.filter((f) => isSupportedImageFile(f))
    if (imageFiles.length === 0) {
      toastUtil.error('이미지 파일만 업로드 가능합니다')
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    const {
      photos: currentPhotos,
      setPhotos,
      setIsReturningFromSubPage,
    } = useReviewStore.getState()
    const remaining = MAX_PHOTOS - currentPhotos.length
    if (remaining <= 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    const normalizedImageFiles: File[] = []
    for (const imageFile of imageFiles) {
      try {
        const normalizedFile = await normalizeImageFile(imageFile)
        normalizedImageFiles.push(normalizedFile)
      } catch (_error) {
        toastUtil.error(HEIF_CONVERT_ERROR_MESSAGE)
      }
    }

    if (normalizedImageFiles.length === 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    if (imageFiles.length === 1) {
      const file = normalizedImageFiles[0]
      const preview = await fileToBase64(file)
      setIsReturningFromSubPage(true)
      navigate(`/photo-edit/${storeId}`, {
        state: { file, preview },
      })
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    const slice = normalizedImageFiles.slice(0, remaining)
    const previews = await Promise.all(slice.map((file) => fileToBase64(file)))
    // 여러 장은 크롭 화면을 건너뛰므로 여기서 허용 비율로 맞춘다.
    // 이미 1:1 · 4:5 · 5:4면 손대지 않는다.
    //
    // 한 장씩 차례로 처리한다. 큰 사진 여러 장을 동시에 캔버스로 그리면
    // 폰에서 메모리가 한꺼번에 치솟아 내보내기가 빈 값으로 실패한다.
    for (const f of slice) {
      await fillVisitDateFromPhoto(f)
    }
    const fittedList: Awaited<ReturnType<typeof autoCropToAllowedAspect>>[] = []
    for (let index = 0; index < slice.length; index++) {
      fittedList.push(
        await autoCropToAllowedAspect(slice[index], previews[index]),
      )
    }
    const newItems: PhotoItem[] = slice.map((file, index) => ({
      id: `${Date.now()}-${index}-${Math.random()}`,
      file: fittedList[index].file,
      preview: fittedList[index].preview,
      description: '',
      // 원본을 남겨둬야 나중에 다시 편집할 수 있다.
      originalPreview: previews[index],
      originalFile: file,
    }))
    setPhotos([...currentPhotos, ...newItems])
    newItems.forEach((it) => void preuploadPhoto(it.id, it.preview))

    if (normalizedImageFiles.length > remaining) {
      toastUtil.warning(
        `최대 ${MAX_PHOTOS}장까지 추가 가능해 일부만 추가되었습니다`,
      )
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemovePhoto = (id: string) => {
    setPhotos(photos.filter((p) => p.id !== id))
  }

  const handleReorderPhotos = (fromIndex: number, toIndex: number) => {
    const next = [...photos]
    const [moved] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, moved)
    setPhotos(next)
  }

  const handleReviewTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const text = e.target.value
    // change 이벤트에서 300자 제한 체크
    if (text.length <= MAX_REVIEW_TEXT_LENGTH) {
      setReviewText(text)
      // 입력 시 오류 메시지 초기화
      if (reviewTextError) {
        setReviewTextError('')
      }
    }
  }

  // 비교 모드에서 카드 선택 (선택 후 바로 다음 단계로 진행)
  const handleStoreSelect = async (selectedStoreId: string) => {
    setSelectedComparisonStoreId(selectedStoreId)
    // 선택 후 바로 다음 단계로 진행
    await handleComparisonNext(selectedStoreId)
  }

  // 비교 모드에서 다음으로 버튼 클릭
  const handleComparisonNext = async (selectedStoreIdParam?: string) => {
    // 이미 한 판이 진행 중이면 무시한다.
    //
    // 카드를 누르면 곧바로 다음 판으로 넘어가는데(handleStoreSelect), 여기엔
    // 재진입 방지가 없었다. 두 번 눌리면 applyZoneComparison이 두 번 돌아
    // 이진탐색이 두 칸 전진하고, 화면에 그릴 가게를 가져오는 요청 두 개가
    // 경합한다. 나중에 도착한 쪽이 화면을 차지하므로 "보고 있는 가게"와
    // "탐색 상태가 아는 가게"가 어긋나고, 그때부터 순위가 엉키거나
    // 이미 봤던 가게가 다시 나올 수 있다.
    if (isLoadingComparisonRef.current) return
    isLoadingComparisonRef.current = true

    const currentSelectedStoreId =
      selectedStoreIdParam ?? selectedComparisonStoreId
    if (!storeId || !compareStores.length || !currentSelectedStoreId) {
      isLoadingComparisonRef.current = false
      toastUtil.error('가게를 선택해주세요')
      return
    }

    try {
      const isCurrentBetter = currentSelectedStoreId === storeId

      // 선택 초기화
      setSelectedComparisonStoreId(null)

      // 이진탐색 한 라운드 적용
      const { done, nextItem } = applyZoneComparison(isCurrentBetter)

      if (done || !nextItem) {
        // 삽입 위치 확정 → 리뷰 저장.
        // 여기는 실제로 오래 걸린다(사진·저장·랭킹 이동)이라 스피너를 켠다.
        setIsLoadingComparison(true)
        await handleComparisonComplete()
        return
      }

      // 다음 상대(랭킹 목록의 다음 중앙값)로 오른쪽 카드만 갈아끼운다.
      //
      // 예전에는 loadComparisonStores로 A·B를 둘 다 서버에서 다시 받아오며
      // 화면 전체를 스피너로 덮었다. A는 안 바뀌는데도 판마다 화면이 사라졌다
      // 나타나서 깜빡였다. 필요한 값(이름·주소·점수)은 랭킹 목록에 이미 있다.
      swapComparisonTarget(nextItem)
    } catch (error) {
      console.error('다음 비교 진행 실패:', error)
      toastUtil.error('다음 비교를 진행할 수 없습니다')
    } finally {
      isLoadingComparisonRef.current = false
      setIsLoadingComparison(false)
    }
  }

  /**
   * 건너뛰기 — 답하지 않고 같은 구간의 다른 상대를 본다.
   *
   * 뽑을 상대가 없으면 스토어가 null을 주고 화면은 그대로 둔다.
   * (버튼도 그때는 흐려져 있다. 여기서 한 번 더 막는 건 판이 넘어가는 도중에
   *  눌린 경우 때문이다.)
   */
  const handleComparisonSkip = () => {
    // 판을 넘기는 중(선택 후 다음 상대를 받는 중)이면 무시한다.
    if (isLoadingComparisonRef.current) return
    const nextItem = skipZoneComparison()
    if (!nextItem) return
    swapComparisonTarget(nextItem)
  }

  /** 뒤로가기 — 직전 한 판(선택이든 건너뛰기든)을 되돌리고 그때 상대를 다시 보여준다. */
  const handleComparisonUndo = () => {
    if (isLoadingComparisonRef.current) return
    const prevItem = undoZoneComparison()
    if (!prevItem) return
    swapComparisonTarget(prevItem)
  }

  // 비교 완료 시 리뷰 저장 및 랭킹 페이지로 이동
  const handleComparisonComplete = async () => {
    if (!storeId) {
      toastUtil.error('가게 정보를 불러오는데 실패했습니다')
      return
    }

    // 한줄평 최대 길이 검증 (선택사항이지만 길이 제한은 있음)
    if (reviewText && reviewText.length > MAX_REVIEW_TEXT_LENGTH) {
      setReviewTextError(
        `한줄평은 최대 ${MAX_REVIEW_TEXT_LENGTH}자까지 작성할 수 있습니다.`,
      )
      return
    }

    // K=3 존+순위: 존과 삽입 위치는 스토어에서 읽음 (점수는 백엔드가 순위로 산출)
    const { currentZone: zoneSnap, insertIndex: insertIndexSnap } =
      useReviewStore.getState()
    const zone = zoneSnap ?? zoneFromScore(experienceScore)
    const insertIndex = insertIndexSnap ?? 0

    setIsLoadingComparison(true)
    try {
      // 사진 처리 (base64 또는 서버 URL)
      //
      // 여기가 사진이 서버로 나가는 유일한 관문이라 크기를 여기서 줄인다.
      // 예전엔 폰 원본을 그대로 base64로 실어 보냈고(장당 약 5MB), 다섯 장이면
      // 요청 하나가 24MB였다. 서버는 그걸 받자마자 가로 1440px로 줄여 저장하므로
      // 올린 바이트의 90%가 버려지는 화소였다. 그 시간이 타임아웃(60초)을 넘겨
      // "사진을 많이 넣을수록 저장이 실패하는" 상태였다. downscalePhoto.ts 참고.
      //
      // 한 장씩 순서대로 처리한다 — 여러 장을 동시에 디코딩하면 WKWebView에서
      // 메모리가 한 번에 치솟는다(여러 장 자동 크롭에서 같은 이유로 순차 처리한다).
      const processedPhotos: Array<{
        id: string
        preview: string
        description: string
      } | null> = []

      for (const photo of photos) {
        // 미리 올려둔 사진이면 주소만 보낸다.
        // 이게 있으면 이 요청에 사진 본문이 안 실려서 저장이 0.1초 수준으로 끝난다.
        if (photo.uploadedUrl) {
          processedPhotos.push({
            id: photo.id,
            preview: photo.uploadedUrl,
            description: photo.description || '',
          })
          continue
        }

        // 이미 서버 URL인 경우 그대로 사용 (다시 올릴 것이 없다)
        if (photo.preview && !photo.preview.startsWith('data:')) {
          processedPhotos.push({
            id: photo.id,
            preview: photo.preview,
            description: photo.description || '',
          })
          continue
        }

        // File 객체가 있으면 base64로 변환
        if (photo.file) {
          const base64 = await fileToBase64(photo.file)
          if (isDecodedImageDataEmpty(base64)) {
            throw new Error(EMPTY_IMAGE_DATA_ERROR_MESSAGE)
          }
          processedPhotos.push({
            id: photo.id,
            preview: await downscaleForUpload(base64),
            description: photo.description || '',
          })
          continue
        }

        // base64 이미지인 경우 그대로 사용
        if (photo.preview && photo.preview.startsWith('data:')) {
          if (isDecodedImageDataEmpty(photo.preview)) {
            throw new Error(EMPTY_IMAGE_DATA_ERROR_MESSAGE)
          }
          processedPhotos.push({
            id: photo.id,
            preview: await downscaleForUpload(photo.preview),
            description: photo.description || '',
          })
          continue
        }

        processedPhotos.push(null)
      }

      // null 값 제거
      const validPhotos = processedPhotos.filter(
        (photo) => photo !== null,
      ) as Array<{
        id: string
        preview: string
        description: string
      }>

      // 리뷰 데이터 저장 (기존 리뷰가 있으면 업데이트, 없으면 생성)
      const response = await reviewApi.createReview({
        placeId: parseInt(storeId),
        // 고칠 기록을 집었으면 그 id를, 새로 남기는 중이면 새 회차임을 알린다.
        // 서버가 (user_id, place_id)로 추측해 덮어쓰지 않게 하는 것이 핵심이다.
        //
        // 값은 스토어의 의도에서 그대로 가져온다. 화면 상태에서 유추하면
        // 하위 화면에 다녀온 뒤 '새로 남기는 중'이 사라져 덮어쓰게 된다.
        reviewId: intent?.mode === 'edit' ? (intent.reviewId ?? undefined) : undefined,
        newVisit: intent?.mode === 'new',
        /*
          이번에 치른 비교 판들. 점수에는 영향을 주지 않는다 — 점수는
          insertIndex가 정한다. 남기는 이유는 "누구와 견줘 어떻게 골랐는지"가
          없으면 비교가 잘 돌고 있는지 알 수 없어서다.
          (2026-06-08 이후 이 기록이 통째로 끊겨 있었다)
        */
        comparisonHistories: useReviewStore
          .getState()
          .comparisonLog.map((r) => ({
            currentPlaceId: parseInt(storeId),
            comparePlaceId: r.comparePlaceId,
            isCurrentBetter: r.isCurrentBetter,
            comparePlaceScore: r.comparePlaceScore,
            comparisonLow: r.comparisonLow,
            comparisonHigh: r.comparisonHigh,
            comparisonCount: r.comparisonCount,
          })),
        zone, // K=3 존 (0/1/2)
        insertIndex, // 존 내 순위 삽입 위치 (점수는 백엔드가 순위로 산출)
        categoryId: selectedCategory
          ? parseInt(selectedCategory.value)
          : undefined,
        reviewText: reviewText ? reviewText.trim() : undefined,
        visitDate: visitDate || undefined,
        waitingTime:
          waitingTime && waitingTime.value !== 'none'
            ? waitingTime.value
            : null, // 웨이팅 시간 (분 단위. 121은 '2시간 이상', null: 없음)
        visitType: visitType, // 방문 타입 (enum 값이 문자열로 변환됨)
        isPublic: is_public, // 공개 여부 (true: 공개, false: 나만보기)
        minPricePerPerson: minPricePerPerson ?? null, // 최소 1인당 가격대
        maxPricePerPerson: maxPricePerPerson ?? null, // 최대 1인당 가격대
        photos: validPhotos.length > 0 ? validPhotos : undefined,
        taggedFriendIds: taggedFriendIds, // 친구 태그 목록 (빈 배열도 전송)
        reviewKeywordIds: reviewKeywordIds, // 리뷰 키워드 ID 목록 (빈 배열도 전송)
      })

      // 홈 피드·랭킹이 들고 있던 목록은 이 기록이 반영되기 전 것이다.
      // 버려야 홈으로 갔을 때 방금 쓴 기록이 보이고, 랭킹도 새 순위로 뜬다.
      invalidateReviewCaches()

      /*
        /collect 에서 담아온 사진은 할 일이 끝났으니 앱 보관함에서 비운다.

        **여기서 지우는 것이 중요하다.** 넘어올 때 지우면, 기록을 쓰다 말고
        나간 사람의 사진이 사라진다. 기록이 실제로 올라간 뒤라야 안전하다.

        id 모양으로 거른다 — 보관함 사진은 '받은시각_번호.확장자'다. 기록
        화면에서 직접 고른 사진은 이 꼴이 아니고, 섞여 들어가도 앱이 그런
        이름의 파일을 못 찾아 아무 일도 일어나지 않는다.

        **확장자는 묶지 않는다.** 전에는 `jpe?g|png` 만 받았는데, 보관함
        이름의 확장자는 원본을 그대로 따른다(shared_photos_store.dart 의
        `_extOf` — 소문자로 바꿀 뿐 종류를 가리지 않는다). 아이폰 사진은
        `.heic` 로 들어오고 갤럭시도 `.webp` 가 섞인다. 그러면 여기서 통째로
        걸러져 **지우라는 말이 아예 안 나간다.** 이름 앞머리(숫자_숫자)만으로도
        우리 보관함 파일인지는 충분히 가려지고, 앱은 제 폴더 안에서 이름이
        정확히 맞는 파일만 지운다(SharedPhotosStore.remove).
      */
      try {
        const collected = useReviewStore
          .getState()
          .photos.map((p) => p.id)
          .filter((id) => /^\d+_\d+\.[A-Za-z0-9]{1,5}$/.test(id))
        const bridge = (window as any).FlutterBridge
        if (collected.length > 0 && typeof bridge?.removeSharedPhotos === 'function') {
          bridge.removeSharedPhotos({ ids: collected })
        }
      } catch (e) {
        // 보관함 정리가 안 돼도 기록은 이미 올라갔다. 막지 않는다.
        console.warn('담아둔 사진 정리 실패', e)
      }

      toastUtil.success('기록이 저장되었습니다')

      // 완료 페이지로 이동 (리뷰 ID를 경로에 포함, redirect 파라미터 전달)
      const redirect = searchParams.get('redirect')
      const reviewCompleteUrl = redirect
        ? `/review-complete/${response.review.id}?redirect=${encodeURIComponent(redirect)}`
        : `/review-complete/${response.review.id}`
      // replace: 작성 페이지를 히스토리에서 제거 — 완료 후 뒤로가기로 재진입 방지
      //
      // 이동을 '먼저' 한다.
      //
      // 예전에는 상태부터 비우고 그다음에 이동했다. 그 사이 React가 한 번 다시
      // 그리는데, 비교 모드 판정(compareStoreIds.length > 0)이 풀리면서 비교 화면이
      // 사라지고 '내용이 전부 지워진 작성 폼'이 한 프레임 보였다.
      // 영수증 화면 직전에 뭔가 깜빡이던 게 이것이다.
      navigate(reviewCompleteUrl, { replace: true })

      // 리셋은 화면을 떠난 뒤에. 이 화면은 이미 사라졌으므로 빈 상태가 보이지 않는다.
      resetReviewData()
      setCurrentStore(null)
      setCompareStores([])
      setSelectedComparisonStoreId(null)
    } catch (error) {
      console.error('리뷰 저장 실패:', error)
      if (
        error instanceof Error &&
        error.message === EMPTY_IMAGE_DATA_ERROR_MESSAGE
      ) {
        toastUtil.error(EMPTY_IMAGE_DATA_ERROR_MESSAGE)
        return
      }
      toastUtil.error('기록 저장에 실패했습니다')
    } finally {
      setIsLoadingComparison(false)
    }
  }

  const renderCategorySelect = () => {
    return (
      <div className="mt-20px px-20px w-full flex flex-col">
        <label className="font-pretendard font-bold text-16px text-brown-80%-main leading-125%">
          카테고리를 선택해주세요
        </label>

        <CustomSelect
          className="mt-10px"
          options={reviewCategoryOptions}
          value={selectedCategory}
          onChange={handleCategoryChange}
          placeholder="카테고리를 선택해주세요"
        />
        <FormErrorMessage error={categoryError} />
      </div>
    )
  }

  const renderStoreInfo = () => {
    // storeInfo가 없으면 렌더링 안 함 (하위 페이지에서 돌아온 경우 전역 스토어 값만 사용)
    if (!storeInfo) {
      return null
    }

    const categoryIcon = getCategoryIconBrown(storeInfo.categoryCode)

    return (
      <div className="mt-20px px-20px w-full flex flex-col">
        {/*
          몇 번째 기록인지.

          기록이 하나뿐인 곳을 고칠 때는 안 붙인다 — "1번째 기록"은 아직
          있지도 않은 2번째를 예고하는 꼴이라 오히려 혼란스럽다. 회차가
          여럿이거나(어느 것을 보고 있는지 알아야 한다) 새로 남기는 중일
          때만 뜬다.

          수정 중이고 고를 회차가 2개 이상이면 눌러서 바꿀 수 있는 버튼이 된다.
        */}
        {visitNumber !== null && (visitNumber > 1 || placeVisits.length > 1) && (
          <div className="mb-8px flex">
            {editingReviewId !== null && placeVisits.length > 1 ? (
              <button
                type="button"
                onClick={() => setShowVisitPicker(true)}
                className="flex items-center gap-5px font-pretendard font-600 text-13px text-textColorText2"
              >
                {visitNumber}번째 기록
                {/*
                  화살표는 글자(▾) 대신 그림으로 둔다. 글자는 기기·폰트마다
                  크기와 베이스라인이 달라 글자와 어긋나 보인다.
                  currentColor라 글자색을 바꾸면 같이 따라온다.
                */}
                <svg
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  className="flex-shrink-0"
                  aria-hidden="true"
                >
                  <path
                    d="M1 1l4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ) : (
              <span className="font-pretendard font-600 text-13px text-textColorText3">
                {visitNumber}번째 기록
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-8px">
          {categoryIcon && (
            <img
              src={categoryIcon}
              alt={storeInfo.category}
              className="w-28px h-28px object-contain"
            />
          )}
          <h1 className="flex-1 font-pretendard font-bold text-16px text-brown-80%-main leading-none truncate">
            {storeInfo.name}
          </h1>
        </div>
        <p className="mt-8px font-pretendard font-regular text-16px text-textColorText2 leading-none truncate">
          {storeInfo.address || ''}
        </p>
      </div>
    )
  }

  const renderExperienceSlider = () => {
    return (
      <div className="mt-20px px-20px w-full flex flex-col">
        <label className="font-pretendard font-700 text-16px text-brown-80%-main leading-125%">
          여기 어땠어요?
        </label>
        <ExperienceButtons
          value={experienceScore}
          onChange={setExperienceScore}
          disabled={isCompareMode}
          addClassName="mt-20px"
        />
      </div>
    )
  }

  const renderNextButton = () => {
    // 필수: 카테고리 선택 + 이용경험 버튼 선택(experienceScore>=0).
    // (이용경험은 더 이상 기본값 없음 — 4버튼 중 하나를 골라야 진행)
    const isDisabled = !selectedCategory || experienceScore < 0

    return (
      <div className="mt-20px px-20px w-full">
        <SubmitButton
          text="다음으로"
          onSubmit={handleNext}
          disabled={isDisabled}
          // 진행 중에는 스피너를 돌리고 두 번째 탭을 막는다.
          loading={isStartingComparison}
          onDisabled={handleNext}
        />
      </div>
    )
  }

  const renderReviewText = () => {
    return (
      <div className="mt-20px px-20px">
        <FormTextarea
          name="reviewText"
          value={reviewText}
          onChange={handleReviewTextChange}
          placeholder="오늘의 미식 경험, 기록으로 남겨보세요"
          error={reviewTextError}
          maxLength={MAX_REVIEW_TEXT_LENGTH}
        />
      </div>
    )
  }

  /**
   * 고른 것들을 한 줄로 줄인다 — 'eulee21 외 1명'.
   * 첫 번째만 이름으로 보여주고 나머지는 수로 센다. 줄이 좁아서 다 못 담는다.
   */
  const summarizeNames = (
    ids: unknown[],
    names: string[],
    unit: string,
  ): string => {
    if (ids.length === 0) return ''
    const picked = names.filter((name) => name.trim().length > 0)
    // 이름을 못 받은 경우에도 고른 사실은 알려준다
    if (picked.length === 0) return `${ids.length}${unit}`
    if (picked.length === 1 && ids.length === 1) return picked[0]
    return `${picked[0]} 외 ${ids.length - 1}${unit}`
  }

  /** '2026-09-10 19:30' → '2026년 9월 10일'. 시간은 빼고 날짜만 보여준다 */
  const summarizeVisitDate = (value: string | null): string => {
    const [year, month, day] = (value ?? '').split(' ')[0].split('-')
    if (!year || !month || !day) return '' // 시간만 고른 경우
    return `${year}년 ${Number(month)}월 ${Number(day)}일`
  }

  /** 가격대는 ₩ 개수로 보여준다. 고른 화면에서 쓰는 표시와 같다 */
  const summarizePrice = (min: number | null, max: number | null): string =>
    // 카페는 눈금이 달라 카테고리를 같이 넘긴다
    getPriceRangeIcon(min, max, storeInfo?.categoryCode ?? storeInfo?.category)

  /*
   * 선택 항목 한 줄.
   *
   * summary가 있으면 화살표 왼쪽에 무엇을 골랐는지 보여준다. 안 고른 항목은
   * 빈칸으로 둔다 — 고른 것만 눈에 들어와야 무엇을 더 채울지 알 수 있다.
   */
  const renderAdditionalOptionButton = (
    title: string,
    handleClick: () => void,
    addClassName?: string,
    summary?: string,
  ) => {
    return (
      <div
        className={`py-6px px-20px w-full flex items-center hover:opacity-70 gap-8px cursor-pointer ${addClassName}`}
        onClick={handleClick}
      >
        <span className="flex-shrink-0 font-pretendard font-bold text-16px text-brown-80%-main text-left">
          {title}
        </span>
        <span className="flex-1 min-w-0 font-pretendard font-regular text-16px text-textColorText3 text-right truncate">
          {summary ?? ''}
        </span>
        <img
          src="/images/chevron-right-icon.png"
          alt="이동"
          className="w-24px h-24px object-contain"
        />
      </div>
    )
  }

  const renderFriendsTag = () => {
    return renderAdditionalOptionButton(
      '친구 태그',
      () => {
        setIsFriendTagBottomSheetOpen(true)
      },
      'mt-20px',
      summarizeNames(taggedFriendIds, taggedFriendNames, '명'),
    )
  }

  const renderVisitDate = () => {
    return renderAdditionalOptionButton(
      '방문일자 선택',
      () => {
        setIsReturningFromSubPage(true) // 하위 페이지 플래그 설정
        navigate(`/visit-date-add`)
      },
      'mt-20px',
      summarizeVisitDate(visitDate),
    )
  }

  const renderPriceRange = () => {
    return renderAdditionalOptionButton(
      '1인당 가격대 기록',
      () => {
        setIsReturningFromSubPage(true) // 하위 페이지 플래그 설정
        navigate('/price-range-add')
      },
      'mt-20px',
      summarizePrice(minPricePerPerson, maxPricePerPerson),
    )
  }

  const renderKeywordSelect = () => {
    return renderAdditionalOptionButton(
      '키워드 선택',
      () => {
        setIsReturningFromSubPage(true) // 하위 페이지 플래그 설정
        navigate('/review-keyword-select')
      },
      'mt-20px',
      summarizeNames(reviewKeywordIds, reviewKeywordNames, '개'),
    )
  }

  const renderAdditionalOptions = () => {
    return (
      <div className="w-full flex flex-col">
        {/* 사진 추가 */}
        {renderPhotos()}

        {/* 한줄평 */}
        {renderReviewText()}

        {/* 친구 태그 */}
        {renderFriendsTag()}

        {/* 방문일자 선택 */}
        {renderVisitDate()}

        {/* 키워드 선택 */}
        {renderKeywordSelect()}

        {/* 1인당 가격대 기록 */}
        {renderPriceRange()}

        {/* 다음으로 버튼 */}
        {renderNextButton()}
      </div>
    )
  }

  const renderVisitType = () => {
    const visitTypes = Object.values(VisitType) as VisitType[]

    return (
      <div className="mt-20px px-20px w-full flex gap-8px">
        {visitTypes.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => setVisitType(type)}
            className={`
                flex-1
                py-12px px-8px
                rounded-8px
                font-pretendard font-700 text-16px leading-20px text-center
                transition-all
                border border 1px border-472410-10%
                shadow-shadow2
                ${
                  visitType === type
                    ? 'bg-brown-80%-main text-white'
                    : 'bg-white text-brown-80%-main'
                }
              `}
          >
            {VISIT_TYPE_NAMES[type]}
          </button>
        ))}
      </div>
    )
  }

  const renderPublicRange = () => {
    return (
      <div className="mt-20px px-20px w-full flex gap-8px">
        <span className="flex-1 font-pretendard font-bold text-16px leading-24px text-brown-80%-main">
          나만 보기
        </span>
        <BoldCheckbox
          size={24}
          checked={!is_public}
          onChange={(checked) => setIsPublic(!checked)}
        />
      </div>
    )
  }

  const renderComparisonOptions = () => {
    if (isLoadingComparison) {
      return (
        <div className="w-full flex items-center justify-center py-40px">
          <div className="flex flex-col items-center gap-12px">
            <LoadingSpinner size={28} borderWidth={3} />
          </div>
        </div>
      )
    }

    if (currentStore && compareStores.length > 0) {
      return (
        <StoreComparison
          store1={currentStore}
          store2={compareStores[0]}
          selectedStoreId={selectedComparisonStoreId}
          onSelect={handleStoreSelect}
          onPeekStart={() => {
            // 사진·텍스트 둘 다 없는(빈) 리뷰면 미리보기를 띄우지 않음(무응답)
            const hasContent =
              !!comparePeekReview?.review_text ||
              (Array.isArray(comparePeekReview?.photos) &&
                comparePeekReview!.photos.length > 0)
            if (hasContent) setIsComparePeekOpen(true)
          }}
          onPeekEnd={() => setIsComparePeekOpen(false)}
          onUndo={handleComparisonUndo}
          onSkip={handleComparisonSkip}
          canUndo={zoneHistory.length > 0}
          canSkip={
            pickSkipPivot(searchLo, searchHi, currentPivot, skippedPivots) !==
            null
          }
        />
      )
    }

    return null
  }

  const renderPhotos = () => {
    return (
      <div className="mt-20px w-full flex flex-col">
        {/* 사진 추가 제목 */}
        <div className="px-20px">
          <span className="font-pretendard font-bold text-16px leading-24px text-brown-80%-main">
            사진 추가
          </span>
        </div>

        {/* 사진 추가 버튼 + 위치 기반 추천 사진 (앱 + 신버전 브리지에서만 노출) */}
        <div
          className="mt-20px px-20px flex gap-8px overflow-x-auto overflow-y-hidden scrollbar-hide"
          style={{
            // overflow-x만 지정하면 CSS 규칙상 overflow-y가 visible→auto로 계산되어
            // 이 줄이 '세로 스크롤 컨테이너'가 된다. 세로로 1px만 넘쳐도 그 안에서
            // 스크롤이 소비되고 페이지로 전달되지 않아 화면이 안 움직인다.
            // 세로는 hidden으로 못 박고, 세로 제스처는 페이지가 받도록 둘 다 허용한다.
            touchAction: 'pan-x pan-y',
            overscrollBehaviorX: 'contain',
          }}
        >
          {photos.length < MAX_PHOTOS && (
            <button
              type="button"
              onClick={handleAddPhotos}
              className={`
                flex-shrink-0
                w-92px h-116px
                rounded-16px
                border border-1px border-4B3621-20%
                bg-472410-5%
                flex items-center justify-center
                hover:opacity-70
              `}
            >
              {/*
                + 만 있는 글리프를 쓰고 크기를 20px 로 줄였다.

                전에는 photo-plus-icon.png 을 w-full 로 깔았는데, 그 자산은
                5% 배경 박스를 품고 있어 이 버튼의 배경·테두리와 겹쳐
                **박스가 두 겹**으로 보였다. 덤으로 + 가 타일 폭의 26% 까지
                커져 혼자 컸다.
              */}
              <img
                src="/images/photo-plus-glyph.png"
                alt="사진 추가"
                className="w-20px h-20px"
              />
            </button>
          )}
          <NearbyPhotoSuggestions
            lat={storeInfo?.lat ?? null}
            lng={storeInfo?.lng ?? null}
            onPickPhoto={handleNearbyPhotoPick}
          />
        </div>

        {photos.length > 0 && (
          <DraggablePhotoList onReorder={handleReorderPhotos}>
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                className="relative flex-shrink-0 w-124px h-155px rounded-16px overflow-hidden bg-472410-5% cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()

                  const editPreview = photo.originalPreview || photo.preview
                  const editFile = photo.originalFile || photo.file

                  if (!editPreview) return

                  // preview가 이미 data: URL이면 바로 사용
                  if (editPreview.startsWith('data:')) {
                    const fileToUse =
                      editFile ||
                      dataURLtoFile(editPreview, `photo-${photo.id}.jpg`)
                    setIsReturningFromSubPage(true)
                    navigate(`/photo-edit/${storeId}`, {
                      state: {
                        file: fileToUse,
                        preview: editPreview,
                        editPhotoId: photo.id,
                        editPhotoIndex: index,
                        initialCropState: photo.cropState,
                      },
                    })
                    return
                  }

                  // preview가 서버 URL이면 서버에서 받아온다.
                  //
                  // 예전에는 <img crossOrigin='anonymous'> 로 불러 캔버스에서
                  // 꺼내려 했다. 그런데 CloudFront가 Access-Control-Allow-Origin 을
                  // 주지 않아(S3 버킷에 CORS 설정 자체가 없다) 로드가 실패했고,
                  // 사용자에겐 "사진을 불러오지 못했습니다"만 떴다. 기존 사진이면
                  // 100% 재현되던 문제다. crossOrigin 을 빼도 캔버스가 오염돼
                  // toDataURL 이 SecurityError 로 죽어 어느 쪽으로도 안 됐다.
                  //
                  // 서버가 S3에서 직접 읽어 넘겨준다. 우리 API는 이미 웹에 CORS
                  // 헤더를 주므로 브라우저 제약이 없다.
                  if (editPreview.startsWith('http')) {
                    reviewApi
                      .getPhotoData(editPreview)
                      .then((base64) => {
                        if (!base64) {
                          toastUtil.error(
                            '사진을 불러오지 못했습니다. 잠시 후 다시 시도해주세요',
                          )
                          return
                        }
                        const fileToUse = dataURLtoFile(
                          base64,
                          `photo-${photo.id}.jpg`,
                        )
                        setIsReturningFromSubPage(true)
                        navigate(`/photo-edit/${storeId}`, {
                          state: {
                            file: fileToUse,
                            preview: base64,
                            editPhotoId: photo.id,
                            editPhotoIndex: index,
                            initialCropState: photo.cropState,
                          },
                        })
                      })
                      .catch(() => {
                        toastUtil.error(
                          '사진을 불러오지 못했습니다. 잠시 후 다시 시도해주세요',
                        )
                      })
                    return
                  }

                  // File 객체가 있으면 base64 변환 후 이동
                  if (editFile) {
                    fileToBase64(editFile)
                      .then((base64) => {
                        setIsReturningFromSubPage(true)
                        navigate(`/photo-edit/${storeId}`, {
                          state: {
                            file: editFile,
                            preview: base64,
                            editPhotoId: photo.id,
                            editPhotoIndex: index,
                            initialCropState: photo.cropState,
                          },
                        })
                      })
                      .catch(console.error)
                  }
                }}
              >
                {photo.preview ? (
                  // 썸네일은 고정 비율(124×155). 크롭 이미지를 object-cover로 꽉 채움.
                  <img
                    src={photo.preview}
                    alt="사진 미리보기"
                    className="w-full h-full object-cover block"
                    onError={(e) => {
                      logStoreReviewPhoto('preview_img_error', storeId, {
                        photoId: photo.id,
                        preview: devDebug.dataUrlMeta(photo.preview),
                      })
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                ) : (
                  <div className="w-124px h-full flex items-center justify-center">
                    <span className="text-style-caption text-textColorText2">
                      이미지 없음
                    </span>
                  </div>
                )}

                {/* 삭제 버튼 */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    handleRemovePhoto(photo.id)
                  }}
                  className={`
                  absolute
                  top-5px right-5px
                  w-24px h-24px
                  flex items-center justify-center
                  hover:opacity-70
                `}
                >
                  <img
                    src="/images/photo-close-icon.png"
                    alt="삭제"
                    className="w-full h-full object-contain"
                  />
                </button>
              </div>
            ))}
          </DraggablePhotoList>
        )}

        {/* hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          className="hidden"
        />

        {/* 오류 메시지 */}
      </div>
    )
  }

  // 바디 렌더링 함수
  const renderBody = () => {
    // 로딩 중
    if (isLoadingStore) {
      return (
        <div className="flex-1 flex items-center justify-center p-24px">
          <div className="flex items-center gap-8px">
            <LoadingSpinner size={28} borderWidth={2} />
          </div>
        </div>
      )
    }

    // 에러 발생 시에만 에러 표시
    if (storeError) {
      return (
        <div className="flex-1 flex items-center justify-center p-24px">
          <p className="text-style-body1 text-error">{storeError}</p>
        </div>
      )
    }

    // 가게 정보가 없으면 에러 표시 (하위 페이지에서 돌아온 경우가 아닐 때만)
    // 하위 페이지에서 돌아온 경우는 전역 스토어 값만 사용하므로 storeInfo가 없어도 정상 동작
    if (!storeInfo && !wasReturningFromSubPage) {
      return (
        <div className="flex-1 flex items-center justify-center p-24px">
          <p className="text-style-body1 text-error">
            가게 정보를 불러오는데 실패했습니다.
          </p>
        </div>
      )
    }

    // 정상 케이스
    return (
      <>
        <div className="app-container flex-1 overflow-y-auto scrollbar-hide">
          <div className="pb-31px w-full flex flex-col">
            {/* 가게 정보 섹션 */}
            {renderStoreInfo()}

            {/* 구분선 */}
            <Divider className="mt-20px px-20px" />

            {/* 카테고리 선택 섹션 */}
            {renderCategorySelect()}

            {/* 방문 타입 (매장/포장·배달) */}
            {renderVisitType()}

            {/* 경험 평가 섹션 */}
            {renderExperienceSlider()}

            {/* 공개범위 설정 */}
            {renderPublicRange()}

            {/* 구분선 */}
            <Divider className="mt-20px px-20px" />

            {/* 비교 모드 또는 추가 옵션 섹션 */}
            {isCompareMode
              ? renderComparisonOptions()
              : renderAdditionalOptions()}
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <AppBar
        title=""
        isBottomBorder
        onBack={
          isCompareMode
            ? () => {
                exitComparisonMode()
                setCurrentStore(null)
                setCompareStores([])
              }
            : undefined
        }
      />

      {/* 가게 평가하기 화면 */}
      {renderBody()}

      {/* A/B 비교 중 B 카드 길게 누르면 내 리뷰 미리보기 (손 떼면 닫힘) */}
      <ReviewPeekOverlay
        open={isComparePeekOpen}
        reviewText={comparePeekReview?.review_text}
        photos={comparePeekReview?.photos}
      />

      {/* 카테고리 추가 모달 */}
      <ReviewCategoryAddModal
        isOpen={isCategoryAddModalOpen}
        onClose={handleCategoryAddModalClose}
        onSuccess={handleCategoryAddSuccess}
      />

      {/* 친구 태그 바텀 시트 */}
      <FriendTagBottomSheet
        isOpen={isFriendTagBottomSheetOpen}
        onClose={() => setIsFriendTagBottomSheetOpen(false)}
        selectedUserIds={taggedFriendIds}
        onUserToggle={toggleTaggedFriend}
      />

      {/* 재방문 — 이미 기록한 곳에 다시 들어왔을 때 */}
      <ExistingRecordDialog
        isOpen={showExistingDialog}
        onEdit={handleEditExisting}
        onNewVisit={handleStartNewVisit}
        // 배경을 눌러 닫으면 수정으로 둔다. 고쳐 쓰는 쪽은 없던 기록을
        // 만들지 않아서, 잘못 눌렀을 때 되돌리기 쉽다.
        onDismiss={handleEditExisting}
      />

      <VisitPickBottomSheet
        isOpen={showVisitPicker}
        visits={placeVisits}
        selectedId={editingReviewId}
        hideScore={hideScore}
        onSelect={handlePickVisit}
        onClose={() => setShowVisitPicker(false)}
      />
    </div>
  )
}

export default StoreReview
